import { useCallback, useEffect, useRef, useState } from 'react'
import env from '@/config/env'
import { getApiHeaders } from '@/config/trpc'
import type {
  StarterFill,
  StarterFillStreamEvent,
} from '@backend/lib/ai/prompts/onboarding/starterFill'

// Drives /stream/moodboard/onboarding-fill: the onboarding starter notes get text
// derived from the user's website, streamed in so the cards can type it. Same NDJSON
// transport as useAnalyzeStream. `done` arrives after the server has written the
// board; the phase only leaves `streaming` once `onSettled` (the caller's refetch)
// has resolved, so the cards never flash their old text in between.

export type StarterFillState = {
  phase: 'idle' | 'streaming' | 'done' | 'error'
  partial: Partial<StarterFill>
}

const IDLE: StarterFillState = { phase: 'idle', partial: {} }

export function useStarterFillStream(moodboardId: number, onSettled: () => Promise<unknown>) {
  const [state, setState] = useState<StarterFillState>(IDLE)
  const abortRef = useRef<AbortController | null>(null)
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  // Leaving the canvas aborts the read; the server finishes and writes regardless.
  useEffect(() => () => abortRef.current?.abort(), [])

  const start = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setState({ phase: 'streaming', partial: {} })

    let outcome: 'done' | 'error' = 'error'
    const apply = (ev: StarterFillStreamEvent) => {
      if (ev.type === 'partial') setState({ phase: 'streaming', partial: ev.fill })
      else outcome = ev.type
    }

    try {
      const response = await fetch(`${env.VITE_BACKEND_URL}/stream/moodboard/onboarding-fill`, {
        method: 'POST',
        headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: moodboardId }),
        signal: controller.signal,
      })
      if (!response.ok || !response.body) throw new Error(`Fill failed (${response.status})`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const flush = (line: string) => {
        if (!line.trim()) return
        try {
          apply(JSON.parse(line) as StarterFillStreamEvent)
        } catch {
          // Malformed line — skip and keep streaming.
        }
      }
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let newlineIdx = buffer.indexOf('\n')
        while (newlineIdx !== -1) {
          flush(buffer.slice(0, newlineIdx))
          buffer = buffer.slice(newlineIdx + 1)
          newlineIdx = buffer.indexOf('\n')
        }
      }
      flush(buffer)
    } catch (error) {
      if (controller.signal.aborted) return
      console.warn('Onboarding starter fill failed:', error)
    }
    if (controller.signal.aborted) return
    // A stream that ends without `done` still has to release the cards (as an error).
    await onSettledRef.current().catch(() => {})
    setState((s) => ({ ...s, phase: outcome }))
  }, [moodboardId])

  return { state, start }
}
